import { InfoCircleOutlined, LeftOutlined, RightOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from '@app/utils/dayjs-setup';
import type { Dayjs } from 'dayjs';
import { Button, Popover, Spin, Tag } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ActiveRental } from '../../types';
import { rentalLengthDays, rentalState, rentalsOnDate, rentedOnDate, type RentalState } from './dayRentals';
import { buildMonthGrid, type MonthGridDay } from './monthGrid';

interface AvailabilityCalendarProps {
  /** The month on show; only its month/year matter. */
  month: Dayjs;
  /** Free units per YYYY-MM-DD, for every box in the grid. */
  freeByDate: ReadonlyMap<string, number>;
  /** The item's rentals overlapping the grid, returned ones included. */
  rentals: readonly ActiveRental[];
  /** Total units held, so a day can read as fully free rather than just "some". */
  owned: number;
  loading: boolean;
  onMonthChange: (month: Dayjs) => void;
}

const localeFor = (language: string): string => (language.toLowerCase().startsWith('el') ? 'el' : 'en-gb');

/**
 * How free a day is, as a tint.
 *
 * Deliberately three states and not a gradient: the decisions taken from this
 * screen are "can I promise this item on that date" (any free) and "is it fully
 * back" (all free). A shade-per-unit would look precise and answer neither, and
 * the count is printed in the box anyway for anyone who needs the exact figure.
 */
const dayTone = (free: number | undefined, owned: number): string => {
  if (free === undefined) return 'bg-white';
  if (free <= 0) return 'bg-red-50';
  if (owned > 0 && free >= owned) return 'bg-emerald-50';
  return 'bg-amber-50';
};

const countTone = (free: number | undefined, owned: number): string => {
  if (free === undefined) return 'text-slate-300';
  if (free <= 0) return 'text-red-600';
  if (owned > 0 && free >= owned) return 'text-emerald-700';
  return 'text-amber-700';
};

const STATE_TAG_COLOR: Record<RentalState, string> = { out: 'processing', overdue: 'error', returned: 'default' };

const DayRentals: React.FC<{ date: string; rentals: readonly ActiveRental[]; locale: string }> = ({
  date,
  rentals,
  locale,
}) => {
  const { t } = useTranslation();
  const today = dayjs().format('YYYY-MM-DD');
  const onDay = rentalsOnDate(rentals, date);
  const formatDate = (value?: string | null) => (value ? dayjs(value).locale(locale).format('D MMM YYYY') : '-');
  const stateLabel: Record<RentalState, string> = {
    out: t('inventory-availability-rental-out'),
    overdue: t('inventory-rental-overdue'),
    returned: t('inventory-availability-rental-returned'),
  };

  if (onDay.length === 0) {
    return <div className="text-sm text-app-text-light">{t('inventory-availability-day-no-rentals')}</div>;
  }
  return (
    <ul className="flex max-h-80 w-80 flex-col divide-y divide-slate-100 overflow-y-auto">
      {onDay.map((rental) => {
        const state = rentalState(rental, today);
        const days = rentalLengthDays(rental);
        const who = rental.recipientName || (rental.recipientId != null ? `#${rental.recipientId}` : '-');
        return (
          <li key={rental.rentalOutId} className="flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate font-medium">
                {who}
                {Number(rental.quantity) > 1 && <span className="text-app-text-light"> × {rental.quantity}</span>}
              </span>
              <Tag className="mr-0" color={STATE_TAG_COLOR[state]}>
                {stateLabel[state]}
              </Tag>
            </div>
            {rental.recipientType && (
              <span className="text-xs text-app-text-light">
                {t(`inventory-recipient-type-${rental.recipientType}`)}
              </span>
            )}
            <span className="text-xs tabular-nums">
              {formatDate(rental.rentalStart)} → {formatDate(rental.effectiveEnd)}
              {days != null && (
                <span className="text-app-text-light">
                  {' '}
                  · {t('inventory-availability-rental-days', { count: days })}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  month,
  freeByDate,
  rentals,
  owned,
  loading,
  onMonthChange,
}) => {
  const { t, i18n } = useTranslation();
  const locale = localeFor(i18n.language);
  const grid = buildMonthGrid(month);
  const [openDate, setOpenDate] = useState<string | null>(null);

  const renderDay = (day: MonthGridDay) => {
    const free = freeByDate.get(day.date);
    const rented = rentedOnDate(rentals, day.date);
    const longDate = dayjs(day.date).locale(locale).format('dddd D MMMM YYYY');
    return (
      <Popover
        key={day.date}
        trigger="click"
        placement="bottom"
        title={longDate}
        open={openDate === day.date}
        onOpenChange={(open) => setOpenDate(open ? day.date : null)}
        content={<DayRentals date={day.date} rentals={rentals} locale={locale} />}
        destroyOnHidden
      >
        <button
          type="button"
          aria-label={t('inventory-availability-day-open', { date: longDate })}
          className={`group flex min-h-[58px] cursor-pointer flex-col border-0 border-r border-b border-solid border-slate-100 px-2 py-1 text-left transition hover:z-10 hover:ring-2 hover:ring-sky-400 hover:ring-inset hover:brightness-95 focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-inset focus-visible:outline-none [&:nth-child(7n)]:border-r-0 ${dayTone(free, owned)} ${
            openDate === day.date ? 'z-10 ring-2 ring-sky-500 ring-inset' : ''
          } ${day.inMonth ? '' : 'opacity-45'}`}
        >
          <div className="flex items-start justify-between">
            <span className={`text-xs tabular-nums ${day.inMonth ? 'text-app-text' : 'text-slate-400'}`}>
              {day.dayOfMonth}
            </span>
            {day.isToday && (
              <span className="rounded bg-sky-600 px-1 text-[10px] font-medium leading-4 text-white">
                {t('inventory-availability-today')}
              </span>
            )}
          </div>
          {free !== undefined && (
            <div className={`text-sm font-semibold tabular-nums ${countTone(free, owned)}`}>{free}</div>
          )}
          {rented > 0 && (
            <span className="mt-auto inline-flex items-center gap-1 self-start rounded-full bg-sky-50 px-1.5 text-[11px] font-medium leading-4 text-sky-700 ring-1 ring-sky-200 transition group-hover:bg-sky-100">
              <UserOutlined />
              {t('inventory-availability-day-rented', { count: rented })}
              <RightOutlined className="text-[9px]" />
            </span>
          )}
        </button>
      </Popover>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Toolbar: navigation on the left, the month it is showing in the middle. */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <Button
            icon={<LeftOutlined />}
            aria-label={t('inventory-availability-prev-month')}
            onClick={() => onMonthChange(month.subtract(1, 'month').startOf('month'))}
          />
          <Button
            icon={<RightOutlined />}
            aria-label={t('inventory-availability-next-month')}
            onClick={() => onMonthChange(month.add(1, 'month').startOf('month'))}
          />
          <Button className="ml-1" onClick={() => onMonthChange(dayjs().startOf('month'))}>
            {t('inventory-availability-this-month')}
          </Button>
        </div>
        <div className="text-base font-semibold">{month.locale(locale).format('MMMM YYYY')}</div>
        <div className="flex flex-wrap items-center justify-end gap-3 text-xs text-app-text-light">
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded border border-emerald-200 bg-emerald-50" />
            {t('inventory-availability-legend-all-free')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded border border-amber-200 bg-amber-50" />
            {t('inventory-availability-legend-some-free')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded border border-red-200 bg-red-50" />
            {t('inventory-availability-legend-none-free')}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-sm text-sky-700">
        <InfoCircleOutlined />
        {t('inventory-availability-click-hint')}
      </div>

      <Spin spinning={loading}>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
            {/* Read off the first week's own dates, so the header is localised and
                Monday-first without a hard-coded weekday list to keep in step. */}
            {(grid.weeks[0] ?? []).map((day) => (
              <div key={day.date} className="px-2 py-1 text-[11px] font-medium text-app-text-light">
                {dayjs(day.date).locale(locale).format('ddd')}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">{grid.weeks.flat().map(renderDay)}</div>
        </div>
      </Spin>
    </div>
  );
};

export default AvailabilityCalendar;
